import { createApi } from '@reduxjs/toolkit/dist/query/react';
import { apiServiceUrl, backendApiServiceUrl, createApiDefaults } from './base';
import { axiosBaseQueryWithAuth } from './axios';
import Cookie from 'js-cookie';
import { sessionOptions } from '../../../auth/session';

// start copy from nodejs repo
// remap the data to nicer API names
type CustomerConfigData = {
  id: number;
  suffix: string;
  name: string;
  homeSite: string;
  homePage: string;
  isContentAI: boolean;
  isDeactivated: boolean;
  deactivatedBy: string;
  deactivatedAt: string;
  isDeleted: boolean;
  deletedBy: string;
  deletedAt: string;
};
// end copy

export const schemaApi = createApi({
  ...createApiDefaults,
  refetchOnFocus: false,
  // the backend has access to snowflake, so we need to do a GET to list all the possible schemas from there
  // the frontend has access to Okta, so we need to do a POST to update the user's schema there
  baseQuery: axiosBaseQueryWithAuth({ baseUrl: '' }),
  reducerPath: 'schemas',
  // TODO is there a use for this tag?
  tagTypes: ['schemas'],

  endpoints: (builder) => ({
    getAvailableSchemas: builder.query<CustomerConfigData[], void>({
      query: () => {
        return {
          url: `${backendApiServiceUrl}auth/user/schema`,
          method: 'GET',
        };
      },
      keepUnusedDataFor: 60, // seconds
      // sort the response
      transformResponse: (baseResponse) => {
        if (!Array.isArray(baseResponse)) {
          return [];
        }
        const availableSchemas = baseResponse as CustomerConfigData[];

        return availableSchemas?.sort((schemaA, schemaB) => schemaA.suffix.localeCompare(schemaB.suffix));
      },
      // redux doesn't know about the cookie
      // so write a fn to use a custom cache key
      // TODO is there something better than re-using the session?
      serializeQueryArgs: () => {
        const sessionCookie = Cookie.get(sessionOptions.cookieName);
        // can't be undefined/null
        return sessionCookie || '';
      },
      providesTags: ['schemas'],
    }),
    updateSelectedSchema: builder.mutation<void, string>({
      query: (newSchema) => {
        return {
          url: `${apiServiceUrl}set_selected_schema`,
          method: 'POST',
          data: {
            selected_schema: newSchema,
          },
        };
      },
    }),
    deactivateSelectedSchema: builder.mutation<void, string>({
      query: (newSchema) => {
        return {
          url: `${backendApiServiceUrl}admin/schema/deactivate`,
          method: 'PUT',
          data: {
            deactivateSchemaName: newSchema,
          },
        };
      },
      invalidatesTags: ['schemas'],
    }),
    dropSelectedSchema: builder.mutation<void, string>({
      query: (newSchema) => {
        return {
          url: `${backendApiServiceUrl}admin/schema/delete`,
          method: 'DELETE',
          data: {
            deactivateSchemaName: newSchema,
          },
        };
      },
      invalidatesTags: ['schemas'],
    }),
    submitUserData: builder.mutation<void, string>({
      query: (data) => {
        return {
          url: `${backendApiServiceUrl}auth/user/schema/create`,
          method: 'POST',
          data,
        };
      },
      invalidatesTags: ['schemas'],
    }),
  }),
});

export const {
  useGetAvailableSchemasQuery,
  useLazyGetAvailableSchemasQuery,
  useUpdateSelectedSchemaMutation,
  useDeactivateSelectedSchemaMutation,
  useDropSelectedSchemaMutation,
  useSubmitUserDataMutation,

  usePrefetch,
  middleware,
  reducer,
  endpoints,
  util,
  reducerPath,
} = schemaApi;
export default schemaApi;
